import { ThemeProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { lazy, Suspense } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';

import { authGuard, routes, unAuthGuard } from './common/utils/routes';
import HiddenPage from './components/HiddenPage/HiddenPage';
import LoadingWrap from './components/Loading/LoadingWrap';
import MessageSnackbar from './components/MessageSnackbar/MessageSnackbar';
import PrivateRoute from './components/Route/PrivateRoute';
import SidebarNav from './components/SidebarNav/SidebarNav';
import store, { persistor } from './store';
import theme from './theme';
import FormulaActivity from './views/formula-activity/FormulaActivity';

const HomeView = lazy(() => import('./views/home/Home'));
const LoginView = lazy(() => import('./views/login/LoginView'));
const SignOut = lazy(() => import('./views/sign-out/SignOut'));
const ChangePassword = lazy(() => import('./views/change-password/ChangePassword'));
const ChangeSecondPassword = lazy(() => import('./views/change-second-code/ChangeSecondPassword'));
const AddFormula = lazy(() => import('./views/add-formula/AddFormula'));
const Warehouse = lazy(() => import('./views/warehouse/Warehouse'));
const WarehouseDetail = lazy(() => import('./views/warehouse-detail/WarehouseDetail'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ThemeProvider theme={theme}>
            <Suspense fallback={<LoadingWrap open />}>
              <BrowserRouter>
                <div className="App">
                  <LoadingWrap />
                  <SidebarNav />
                  <MessageSnackbar />
                  <Routes>
                    <Route path={routes.LOGIN} element={<PrivateRoute guards={[unAuthGuard]} element={<LoginView />} />} />

                    <Route
                      path={routes.WAREHOUSE}
                      element={<PrivateRoute guards={[authGuard]} element={<HiddenPage component={<Warehouse />} />} />}
                    />
                    <Route
                      path={routes.WAREHOUSE_DETAIL}
                      element={<PrivateRoute guards={[authGuard]} element={<HiddenPage component={<WarehouseDetail />} />} />}
                    />

                    <Route path={routes.ADD_FORMULA} element={<PrivateRoute guards={[authGuard]} element={<AddFormula />} />} />
                    <Route path={routes.EDIT_FORMULA} element={<PrivateRoute guards={[authGuard]} element={<AddFormula />} />} />
                    <Route path={routes.SIGN_OUT} element={<PrivateRoute guards={[authGuard]} element={<SignOut />} />} />
                    <Route path={routes.CHANGE_PASSWORD} element={<PrivateRoute guards={[authGuard]} element={<ChangePassword />} />} />
                    <Route
                      path={routes.CHANGE_PIN_CODE}
                      element={<PrivateRoute guards={[authGuard]} element={<ChangeSecondPassword />} />}
                    />
                    <Route
                      path={routes.FORMULA_ACTIVITY}
                      element={<PrivateRoute guards={[authGuard]} element={<HiddenPage component={<FormulaActivity />} />} />}
                    />
                    <Route path={routes.DEFAULT} element={<PrivateRoute guards={[authGuard]} element={<HomeView />} />} />
                  </Routes>
                </div>
              </BrowserRouter>
            </Suspense>
          </ThemeProvider>
        </PersistGate>
      </Provider>
    </QueryClientProvider>
  );
};

export default App;
